import { Map as ImmutableMap } from "immutable";
import { useEffect, useMemo, useState } from "react";
import { VelavuSite } from "velavu-js-api";
import {
	MappedinMVFFloor,
	MappedinMVFVenue,
	downloadAndParseMappedinVenueMVF,
} from "./mappedin/mappedin-mvf";
import { getFloorTarget } from "./floor-helper";
import { useMappedinIntegrationContext } from "../components/main/mappedin-integration-provider";
import whitelabel from "../whitelabel";

export type MappedinSiteRenderData = {
	// The ID of the Mappedin venue
	venueID: string;
} & (
	| {
			status: "loading";
	  }
	| {
			status: "success";
			venue: MappedinMVFVenue;
	  }
	| {
			status: "error";
			error: unknown;
	  }
);

/**
 * Lazily loads and maintains up-to-date Mappedin site render data for the given Velavu site ID.
 * Caches downloaded data for quick access later.
 * @param activeSiteIDs The IDs of the Velavu sites to load Mappedin site render data for.
 */
export function useMappedinSiteRenderData(
	activeSiteIDs: readonly string[],
): Map<string, MappedinSiteRenderData> {
	const { accessToken: mappedinAccessToken, siteVenueMap } =
		useMappedinIntegrationContext();

	const [cachedVenues, setCachedVenues] =
		useState(ImmutableMap<string, MappedinSiteRenderData>());

	//Download and cache Mappedin venue data for the given site IDs
	useEffect(() => {
		//Wait until we have sites to download and Mappedin access
		if (activeSiteIDs.length === 0 || mappedinAccessToken === undefined) {
			return;
		}

		const venueIDsToDownload = new Set<string>();
		for (const activeSiteID of activeSiteIDs) {
			//Look up the Mappedin venue ID for the given site ID
			const venueID = siteVenueMap.get(activeSiteID);
			if (venueID === undefined) {
				continue;
			}

			//Make sure we are not already downloading the Mappedin venue data
			if (!cachedVenues.has(venueID)) {
				venueIDsToDownload.add(venueID);
			}
		}

		if (venueIDsToDownload.size === 0) {
			return;
		}

		//Mark that we are downloading the Mappedin venue data
		setCachedVenues((currentVenues) => {
			return currentVenues.withMutations((mutableVenues) => {
				for (const venueID of venueIDsToDownload) {
					if (!mutableVenues.has(venueID)) {
						mutableVenues.set(venueID, {
							status: "loading",
							venueID: venueID,
						});
					}
				}
			});
		});

		for (const venueID of venueIDsToDownload) {
			//Download the Mappedin venue data
			console.log("Downloading Mappedin venue data for venueID", venueID);
			downloadAndParseMappedinVenueMVF(
				venueID,
				mappedinAccessToken,
				whitelabel.mappedin?.styleCollection,
			)
				.then((venue) => {
					setCachedVenues((currentVenues) =>
						currentVenues.set(venueID, {
							status: "success",
							venueID: venueID,
							venue: venue,
						}),
					);
				})
				.catch((error) => {
					setCachedVenues((currentVenues) =>
						currentVenues.set(venueID, {
							venueID: venueID,
							status: "error",
							error: error,
						}),
					);
				});
		}
	}, [
		activeSiteIDs,
		mappedinAccessToken,
		siteVenueMap,
		cachedVenues,
		whitelabel.mappedin?.styleCollection,
	]);

	return useMemo(() => {
		const result = new Map<string, MappedinSiteRenderData>();
		for (const activeSiteID of activeSiteIDs) {
			//Get the Mappedin venue ID for the given site ID
			const venueID = siteVenueMap.get(activeSiteID);
			if (venueID === undefined) {
				continue;
			}

			//Get the render data for the venue
			const renderData = cachedVenues.get(venueID);
			if (renderData === undefined) {
				continue;
			}

			result.set(activeSiteID, renderData);
		}
		return result;
	}, [activeSiteIDs, siteVenueMap, cachedVenues]);
}

/**
 * Maps a Velavu floor ID to a Mappedin floor ID
 * @param siteID The ID of the Velavu site
 * @param floorID The ID of the Velavu floor
 * @returns The Mappedin floor ID
 */
export function velavuFloorIDToMappedinFloorID(
	siteID: string,
	floorID: string,
): string | undefined {
	if (!floorID.startsWith(siteID)) {
		return undefined;
	}

	const mappedinFloorID = floorID.slice(siteID.length);
	return mappedinFloorID.length === 0 ? undefined : mappedinFloorID;
}

/**
 * Selects a Mappedin floor from a Mappedin venue for the given Velavu site and floor ID
 * @param venue The Mappedin venue
 * @param velavuSiteID The ID of the Velavu site
 * @param velavuFloorID The ID of the Velavu floor
 * @returns The Mappedin floor or undefined if not found
 */
export function selectMappedinVenueFloor(
	venue: MappedinMVFVenue,
	velavuSiteID: string,
	velavuFloorID: string,
): MappedinMVFFloor | undefined {
	//Get the Mappedin floor ID for the given Velavu floor ID
	const mappedinFloorID = velavuFloorIDToMappedinFloorID(
		velavuSiteID,
		velavuFloorID,
	);
	if (mappedinFloorID === undefined) {
		return undefined;
	}

	//Return the Mappedin floor
	return venue.floors[mappedinFloorID];
}

export interface MappedinFloorRenderData {
	siteID: string;
	floor: MappedinMVFFloor;
}

/**
 * Downloads and maintains up-to-date Mappedin floor data for the given Velavu sites and floor IDs
 * @param activeSites The active Velavu sites
 * @param floorOverride The floor selection override object to use
 * @returns The Mappedin floors that have loaded and can be displayed
 */
export function useMappedinFloor(
	activeSites: ReadonlyArray<VelavuSite | undefined>,
	floorOverride?: Readonly<{ [key: string]: string | undefined }>,
): MappedinFloorRenderData[] {
	//Deduplicate the active sites
	const dedupedActiveSites = useMemo((): VelavuSite[] => {
		const result: VelavuSite[] = [];
		const siteIDs = new Set<string>();
		for (const activeSite of activeSites) {
			if (activeSite === undefined || siteIDs.has(activeSite.id)) {
				continue;
			}
			siteIDs.add(activeSite.id);
			result.push(activeSite);
		}
		return result;
	}, [activeSites]);

	//Get the IDs of the active sites
	const activeSiteIDs = useMemo(
		() => dedupedActiveSites.map((activeSite) => activeSite.id),
		[dedupedActiveSites],
	);

	//Get the Mappedin data for the active sites
	const mappedinSiteRenderData = useMappedinSiteRenderData(activeSiteIDs);

	return useMemo(() => {
		const mappedinFloors: MappedinFloorRenderData[] = [];

		for (const activeSite of dedupedActiveSites) {
			//Resolve the selected floor
			const resolvedFloor = getFloorTarget(
				activeSite.floors,
				floorOverride?.[activeSite.id],
			);
			if (resolvedFloor === undefined) {
				continue;
			}

			//Wait until we have Mappedin data
			const siteRenderData = mappedinSiteRenderData.get(activeSite.id);
			if (
				siteRenderData === undefined ||
				siteRenderData.status !== "success"
			) {
				continue;
			}

			//Return the Mappedin floor
			const mappedinFloor = selectMappedinVenueFloor(
				siteRenderData.venue,
				activeSite.id,
				resolvedFloor.id,
			);
			if (mappedinFloor !== undefined) {
				mappedinFloors.push({
					siteID: activeSite.id,
					floor: mappedinFloor,
				});
			}
		}

		return mappedinFloors;
	}, [dedupedActiveSites, mappedinSiteRenderData, floorOverride]);
}
